import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import { checkInstacartRetailer, shouldShowAdvertisingPortolio } from 'src/utils/app';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { FluentSearchFilterBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesDetails/SearchBuilder';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { formatToPlural } from 'src/utils/stringFormatting';
import {
  AdClickField,
  AdSpendField,
  AdSalesField,
  ROASField,
  CPCField,
  getRetailerKey,
  retailerCustomExportFields
} from './constants';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { AdvertisingMetricSubtitle } from './AdMetricSubtitle';
import { useDebouncedValue, useAppSelector } from 'src/utils/Hooks';
import {
  AdGroupColumnDefinition,
  CampaignColumnDefinition,
  EntityColumnDefinition,
  PortfolioColumnDefinition,
  ProductColumnDefinition,
  TargetColumnDefinition,
  CampaignTypeField,
  CampaignBudgetField,
  CampaignAutomationField,
  PortfolioBudgetField,
  PortfolioAutomationField,
  PortfolioProjectedSpendField
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

export enum AdMetricDropDownOptions {
  Entity = 'entityId',
  Campaign = 'campaignId',
  AdGroup = 'adGroupId',
  Portfolio = 'portfolioId',
  Target = 'targetingText',
  Product = 'stacklineSku'
}

const adMetricDropdownOptionToColumnDefinition = {
  [AdMetricDropDownOptions.Entity]: EntityColumnDefinition,
  [AdMetricDropDownOptions.Campaign]: [
    CampaignColumnDefinition,
    CampaignTypeField,
    CampaignBudgetField,
    CampaignAutomationField
  ],
  [AdMetricDropDownOptions.AdGroup]: AdGroupColumnDefinition,
  [AdMetricDropDownOptions.Portfolio]: [
    PortfolioColumnDefinition,
    PortfolioBudgetField,
    PortfolioAutomationField,
    PortfolioProjectedSpendField
  ],
  [AdMetricDropDownOptions.Target]: TargetColumnDefinition,
  [AdMetricDropDownOptions.Product]: ProductColumnDefinition
};

export const adMetricDropdownOptionToEntity = {
  [AdMetricDropDownOptions.Entity]: { displayName: 'Entity', entityType: 'adEntity' },
  [AdMetricDropDownOptions.Campaign]: { displayName: 'Campaign', entityType: 'adCampaign' },
  [AdMetricDropDownOptions.AdGroup]: { displayName: 'Ad Group', entityType: 'adGroup' },
  [AdMetricDropDownOptions.Portfolio]: { displayName: 'Portfolio', entityType: 'adPortfolio' },
  [AdMetricDropDownOptions.Target]: { displayName: 'Target', entityType: 'adTarget' },
  [AdMetricDropDownOptions.Product]: { displayName: 'Product', entityType: 'product' }
};

const getClientGroupByFields = () => {
  const isInstacartRetailer = checkInstacartRetailer();

  const groupByOptions = [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Entity,
    AdMetricDropDownOptions.Portfolio,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.Product
  ];

  if (isInstacartRetailer) {
    return groupByOptions.filter((option) => option !== AdMetricDropDownOptions.Portfolio);
  } else {
    return groupByOptions;
  }
};

const groupByFieldsByEntityType = {
  client: getClientGroupByFields(),
  adEntity: [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Portfolio,
    AdMetricDropDownOptions.Product,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.AdGroup
  ],
  adPortfolio: [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Product,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.AdGroup
  ],
  adCampaign: [AdMetricDropDownOptions.Product, AdMetricDropDownOptions.Target, AdMetricDropDownOptions.AdGroup],
  brand: [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Product,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.AdGroup
  ],
  category: [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Product,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.AdGroup
  ],
  subcategory: [
    AdMetricDropDownOptions.Campaign,
    AdMetricDropDownOptions.Product,
    AdMetricDropDownOptions.Target,
    AdMetricDropDownOptions.AdGroup
  ],
  product: [AdMetricDropDownOptions.Campaign, AdMetricDropDownOptions.Target, AdMetricDropDownOptions.AdGroup],
  adTarget: [AdMetricDropDownOptions.Product, AdMetricDropDownOptions.Target, AdMetricDropDownOptions.AdGroup]
};

const FieldsForEntityType = {
  [AdMetricDropDownOptions.Entity]: [AdClickField, AdSpendField, AdSalesField, ROASField, CPCField],
  [AdMetricDropDownOptions.Campaign]: [AdClickField, AdSpendField, AdSalesField, ROASField],
  [AdMetricDropDownOptions.AdGroup]: [AdClickField, AdSpendField, AdSalesField, ROASField, CPCField],
  [AdMetricDropDownOptions.Portfolio]: [AdClickField, AdSpendField, AdSalesField, ROASField],
  [AdMetricDropDownOptions.Target]: [AdClickField, AdSpendField, AdSalesField, ROASField, CPCField],
  [AdMetricDropDownOptions.Product]: [AdClickField, AdSpendField, AdSalesField, ROASField, CPCField]
};

const buildFilter = {
  [AdMetricDropDownOptions.Entity]: [{ fieldName: 'entityName', values: [] }],
  [AdMetricDropDownOptions.Campaign]: [{ fieldName: 'campaignName', values: [] }],
  [AdMetricDropDownOptions.AdGroup]: [{ fieldName: 'adGroupName', values: [] }],
  [AdMetricDropDownOptions.Portfolio]: [{ fieldName: 'portfolioName', values: [] }],
  [AdMetricDropDownOptions.Target]: [{ fieldName: 'targetingText', values: [] }],
  [AdMetricDropDownOptions.Product]: [{ fieldName: 'title', values: [] }]
};

const AdMetricGrid = () => {
  const entityType = useMainEntityType();
  const [dropdownOption, setDropdownOption] = useState<AdMetricDropDownOptions>(
    groupByFieldsByEntityType[entityType][0]
  );
  const { beaconNavigator } = useBeaconNavigator();
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);
  const { startDayId: comparisonStartDayId, endDayId: comparisonEndDayId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const { adPortfolios } = useAppSelector((state) => state);
  const { name: retailer, id: retailerId } = useAppSelector((state) => state?.retailer);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchText);

  const SearchBuilder = useMemo(() => new FluentSearchFilterBuilder(buildFilter), []);

  const buildSearch = useCallback(() => {
    const selector = () => {
      return {
        select: (filters: any) => filters[dropdownOption]
      };
    };

    if (searchText === '') {
      return [];
    }

    const normalizedSearch = debouncedSearchValue.trim().replace(/\s+/g, ' ');
    const tokens = normalizedSearch
      .split(' ')
      .map((token) => token.replace(/[^a-zA-Z0-9]/g, ''))
      .filter(Boolean);

    let ret = SearchBuilder.setSearchTerm(normalizedSearch).withSelector(selector()).build();

    if (tokens.length > 1) {
      ret = tokens.flatMap((token) => SearchBuilder.setSearchTerm(token).withSelector(selector()).build());
    }

    return ret;
  }, [SearchBuilder, debouncedSearchValue, dropdownOption, searchText]);

  useEffect(() => {
    setSearchText('');
  }, [dropdownOption]);
  const showPortfolio =
    dropdownOption === AdMetricDropDownOptions.Portfolio && shouldShowAdvertisingPortolio && retailerId === '2';

  return (
    <div>
      <AdvertisingMetricSubtitle
        option={dropdownOption}
        setOption={setDropdownOption}
        options={groupByFieldsByEntityType[entityType].map((option) => ({
          name: option,
          displayName: adMetricDropdownOptionToEntity[option].displayName
        }))}
        builderProps={{
          groupBy: dropdownOption,
          requestId: 'adMetricGrid',
          fields: retailerCustomExportFields[getRetailerKey(retailer)] ?? [],
          overrideDependentFields: true,
          buildRequest: (builder: AdvancedSearchRequestBuilder) => {
            return builder
              .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
              .removeConditionRangeFilters((builderFilter) => builderFilter.fieldName === 'weekId')
              .addConditionRangeFilter('dayId', startDayId, endDayId)
              .setTab('advertising')
              .setCustomAdditionalMetadata({ derivedFieldsJson: '[]', useNewExports: 'True' });
          },
          buildAggregation: (builder: AggregationBuilder) => {
            return builder
              .addConditionRangeFilter('dayId', startDayId, endDayId)
              .removeConditionTermFilter('weekId')
              .removeConditionRangeFilters('weekId');
          }
        }}
      />
      <div
        style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <SearchTextField
          placeholder={`Search ${formatToPlural(
            adMetricDropdownOptionToEntity[dropdownOption].displayName.toLowerCase()
          )}`}
          onSubmit={(e) => {
            e.preventDefault();
          }}
          onChange={(e) => {
            e.preventDefault();
            setSearchText(e.target.value);
          }}
        />
        {showPortfolio && (
          <SlButton
            onClick={() => {
              beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'portfolioSettings', wr: '4w' });
              beaconNavigator.go();
            }}
            variant="contained"
          >
            + Add Portfolio
          </SlButton>
        )}
      </div>
      <MetricListContainer
        skeletonRows={20}
        showHeader={false}
        renderGrid={(args) => <ProductGrid {...args} />}
        headColumnDefinition={adMetricDropdownOptionToColumnDefinition[dropdownOption]}
        entityTableHeaderProps={{
          prefixTitle: 'Ad Metric by',
          title: ''
        }}
        indexName={GetAdMetricIndexName(entityType, adMetricDropdownOptionToEntity[dropdownOption].entityType)}
        fields={FieldsForEntityType[dropdownOption] || []}
        useTableDataProps={{
          customComparisonStartWeekId: +comparisonStartDayId,
          customComparisonEndWeekId: +comparisonEndDayId,
          buildRequest: (builder: AdvancedSearchRequestBuilder) => {
            const ret = builder
              .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
              .removeConditionRangeFilters((builderFilter) => builderFilter.fieldName === 'weekId')
              .addConditionRangeFilter('dayId', startDayId, endDayId);
            if (debouncedSearchValue !== '') {
              const nestedFilters = buildSearch();
              const nestedCondition = nestedFilters.length > 1 ? 'must' : 'should';
              ret.addNestedFilterCondition(nestedCondition, nestedFilters);
            }
            return ret;
          },
          buildAggregation: (builder: AggregationBuilder) => {
            return builder
              .addConditionRangeFilter('dayId', startDayId, endDayId)
              .removeConditionTermFilter('weekId')
              .removeConditionRangeFilters('weekId')
              .clearComparisonRangeFilters()
              .addComparisonRangeFilter('dayId', +comparisonStartDayId, +comparisonEndDayId);
          },
          groupByFieldName: dropdownOption,
          filterResponse: () => true
        }}
        sortFieldName="clicks"
        showGrid={false}
        postProcessData={(data) => {
          const portfolioIdSet = new Set(data.map((portfolio) => portfolio.portfolioId));
          const additionalPortfolios = adPortfolios
            .filter((portfolio) => !portfolioIdSet.has(portfolio.id))
            .map((portfolio: any) => {
              return {
                ...portfolio?.extendedAttributes,
                portfolio: { portfolioName: portfolio?.extendedAttributes?.name }
              };
            });

          if (dropdownOption === AdMetricDropDownOptions.Portfolio && entityType !== 'adEntity') {
            return data.concat(additionalPortfolios);
          }
          return data;
        }}
      />
    </div>
  );
};

export default AdMetricGrid;
